import { useEffect, useEffectEvent } from 'react';

import { useInject } from '@vk-hr-tek/core/ioc';
import { Socket } from '@vk-hr-tek/core/websocket';

interface SubscriptionFallbackOptions {
  /** Подписаться через WS. Возвращает функцию отписки. */
  subscribe: () => () => void;
  /** Запрос для polling-фолбэка (диспатч thunk, REST-запрос и т.п.). */
  poll: () => void;
  /** Интервал polling в миллисекундах. */
  pollInterval: number;
  /** Триггер пересоздания подписки (например, eventId). */
  key?: string;
  /** Если false — хук бездействует (ни WS, ни polling). */
  enabled?: boolean;
}

/**
 * Универсальный хук для подписки с polling-фолбэком.
 *
 * Логика:
 * - WS живёт → активна WS-подписка, polling выключен.
 * - WS отвалился (onclose, resetWs, неподдерживаемый браузер) → запускается polling.
 * - WS вернулся (onopen) → polling останавливается.
 * - Хук размонтирован → всё убирается.
 *
 * Если хук не вызван — ни WS-подписки, ни polling нет.
 */
export const useSubscriptionWithPollingFallback = ({
  subscribe,
  poll,
  pollInterval,
  key,
  enabled = true,
}: SubscriptionFallbackOptions) => {
  const socket = useInject(Socket);

  const onSubscribe = useEffectEvent(subscribe);
  const onPoll = useEffectEvent(poll);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    let unsubscribe: (() => void) | null = null;
    let pollTimer: ReturnType<typeof setInterval> | null = null;

    const stopPolling = () => {
      if (pollTimer) {
        clearInterval(pollTimer);
        pollTimer = null;
      }
    };

    const startPolling = () => {
      if (pollTimer) {
        return;
      }
      onPoll();
      pollTimer = setInterval(onPoll, pollInterval);
    };

    const handleConnectionStatus = (isConnected: boolean) => {
      if (isConnected) {
        stopPolling();
        if (!unsubscribe) {
          unsubscribe = onSubscribe();
          onPoll();
        }
      } else {
        if (unsubscribe) {
          unsubscribe();
          unsubscribe = null;
        }
        startPolling();
      }
    };

    const unsubscribeStatus = socket.onConnectionStatus(handleConnectionStatus);

    if (socket.isConnected()) {
      unsubscribe = onSubscribe();
      onPoll();
    } else {
      startPolling();
    }

    return () => {
      unsubscribeStatus();
      stopPolling();
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [socket, key, enabled, pollInterval]);
};
